import { Button } from '@src/components/ui/button';
import { Form } from '@src/components/ui/form';

import { ProcessFieldDynamic } from '@shared/components/ProcessFieldDynamic/ProcessFieldDynamic';
import useCurrentLang from '@shared/hooks/useCurrentLang';
import {
    ServiceFieldTableRowDto,
    ServiceProcessFieldTableColumnVM,
} from '@shared/types/process';

import { buildProcessFieldFromTableColumn } from './buildProcessFieldFromTableColumn';
import { TableFieldMobileRow } from './TableFieldMobileRow';
import {
    formatCellDisplayValue,
    formatTableCellDisplayValue,
    getColumnTitle,
    isColumnEditable,
    isTableColumnReadOnly,
} from './tableFieldUtils';

import { ShadcnButton, ShadcnForm } from '@shadcn-features/core';
import { SquareCheckBig, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    row: ServiceFieldTableRowDto;
    columns: ServiceProcessFieldTableColumnVM[];
    disabled?: boolean;
    focusFirstField?: boolean;
    lang: ReturnType<typeof useCurrentLang>;
    onSave: (cells: Record<string, unknown>) => void;
    onCancel: () => void;
    onDelete: () => void;
    onFocusFirstFieldHandled?: () => void;
    mobile?: boolean;
    rowNumber?: number;
};

function TableFieldEditableCell({
    column,
    lang,
    autoFocus,
    disabled,
    showLabel,
}: {
    column: ServiceProcessFieldTableColumnVM;
    lang: ReturnType<typeof useCurrentLang>;
    autoFocus?: boolean;
    disabled?: boolean;
    showLabel?: boolean;
}) {
    const cellRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!autoFocus) {
            return;
        }

        const frameId = requestAnimationFrame(() => {
            const focusable = cellRef.current?.querySelector<HTMLElement>(
                'input:not([type="hidden"]), textarea, select, button[role="combobox"]',
            );

            focusable?.focus();
        });

        return () => cancelAnimationFrame(frameId);
    }, [autoFocus]);

    if (isTableColumnReadOnly(column)) {
        return (
            <span className='text-card-text text-sm'>
                {formatTableCellDisplayValue(column, undefined, lang) || '—'}
            </span>
        );
    }

    const field = buildProcessFieldFromTableColumn(column);

    if (!field) {
        return (
            <span className='text-card-text text-sm'>
                {formatCellDisplayValue(undefined) || '—'}
            </span>
        );
    }

    return (
        <div ref={cellRef}>
            <ProcessFieldDynamic
                field={field}
                label={showLabel ? getColumnTitle(column, lang) : null}
                name={column.id}
                disabled={disabled}
                fieldProps={
                    showLabel
                        ? { 'aria-label': getColumnTitle(column, lang) }
                        : undefined
                }
            />
        </div>
    );
}

export function TableFieldEditingRowCells({
    row,
    columns,
    disabled,
    focusFirstField,
    lang,
    onSave,
    onCancel,
    onDelete,
    onFocusFirstFieldHandled,
    mobile,
    rowNumber = 1,
}: Props) {
    const { t } = useTranslation();
    const rowForm = ShadcnForm.useForm<Record<string, unknown>>({
        defaultValues: row.cells ?? {},
    });

    const firstEditableColumnId = useMemo(() => {
        return columns.find(
            (column) =>
                isColumnEditable(column) &&
                buildProcessFieldFromTableColumn(column),
        )?.id;
    }, [columns]);

    useEffect(() => {
        if (focusFirstField) {
            onFocusFirstFieldHandled?.();
        }
    }, [focusFirstField, onFocusFirstFieldHandled]);

    if (mobile) {
        return (
            <TableFieldMobileRow
                row={row}
                columns={columns}
                rowNumber={rowNumber}
                disabled={disabled}
                onCancel={onCancel}
                onDelete={onDelete}
                onSave={() => {
                    void rowForm.handleSubmit(onSave)();
                }}
            >
                <Form {...rowForm}>
                    <div className='table-field-mobile-edit flex flex-col gap-[13px] [--form-item-margin-bottom:0px]'>
                        {columns.map((column) => (
                            <div key={column.id} className='min-w-0'>
                                {isTableColumnReadOnly(column) ? (
                                    <>
                                        <div className='mb-1.5 text-xs leading-4 font-semibold'>
                                            {getColumnTitle(column, lang)}
                                        </div>
                                        <span className='text-sm'>
                                            {formatTableCellDisplayValue(
                                                column,
                                                row.cells?.[column.id],
                                                lang,
                                            ) || '—'}
                                        </span>
                                    </>
                                ) : (
                                    <TableFieldEditableCell
                                        showLabel
                                        column={column}
                                        lang={lang}
                                        disabled={disabled}
                                        autoFocus={
                                            focusFirstField &&
                                            column.id === firstEditableColumnId
                                        }
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </Form>
            </TableFieldMobileRow>
        );
    }

    return (
        <>
            {columns.map((column) => (
                <td key={column.id} className='table-field-grid__cell'>
                    <Form {...rowForm}>
                        <TableFieldEditableCell
                            column={column}
                            lang={lang}
                            autoFocus={
                                focusFirstField &&
                                column.id === firstEditableColumnId
                            }
                        />
                    </Form>
                </td>
            ))}
            <td className='table-field-grid__cell md:px-3'>
                <div className='flex items-center justify-center gap-2'>
                    <Button
                        className='border-border size-8 shrink-0 border p-0 md:bg-(--color-body) md:text-(--color-brand-green)'
                        paddingX={0}
                        paddingY={0}
                        aria-label={t('translation:save')}
                        disabled={disabled}
                        size='sm'
                        type='button'
                        variant='outline'
                        onClick={() => {
                            void rowForm.handleSubmit((values) =>
                                onSave(values),
                            )();
                        }}
                    >
                        <SquareCheckBig className='size-4' />
                    </Button>
                    <Button
                        className='border-border size-8 shrink-0 border p-0 md:hidden'
                        paddingX={0}
                        paddingY={0}
                        aria-label={t('translation:cancel')}
                        disabled={disabled}
                        size='sm'
                        type='button'
                        variant='outline'
                        onClick={onCancel}
                    >
                        <X className='size-4' />
                    </Button>
                    <ShadcnButton
                        className='border-border hidden size-8 shrink-0 border p-0 md:inline-flex md:bg-(--color-body)'
                        paddingX={0}
                        paddingY={0}
                        aria-label={t('translation:delete')}
                        disabled={disabled}
                        size='sm'
                        type='button'
                        variant='outline'
                        icon={
                            <Trash2 className='size-4 text-(--color-danger)' />
                        }
                        popconfirm={{
                            title: t('translation:areYouSure'),
                            onConfirm: onDelete,
                        }}
                    />
                </div>
            </td>
        </>
    );
}
